import React, { memo, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import * as R from 'ramda';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  ADD_TO_GROUP_TRAY,
  ENTITY_TRAY,
  LAYER_TRAY,
  GROUP_TRAY,
  OVERLAP_TRAY,
  RECENT_TRAY,
  SEARCH_TRAY,
  FILTER_TRAY,
  DETAILS_TRAY
} from '@constants/map-trays';

import { paper } from '@constants/mui-theme';

import { useWatchOverlapEntitiesCache } from '@components/map/tray/hooks';
import Details from '@components/map/tray/details';
import Entity from '@components/map/tray/entity';
import Filter from '@components/map/tray/filter';
import Group from '@components/map/tray/group';
import Layer from '@components/map/tray/layer';
import Recent from '@components/map/tray/recent';
import Search from '@components/map/tray/search';
import OverlapTray from '@components/map/tray/overlap/overlap-tray';
import AddToGroupTray from '@components/map/tray/overlap/add-to-group-tray';

import { getTrayTop, getTrayDepth } from '@selectors/map-selector';
import { openEntityTray, openFilterTray } from '@actions/map-actions';

import { isMapPage } from '@utils/filter-utils';

import styles from './tray.scss'; // eslint-disable-line css-modules/no-unused-class

const Tray = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const tray = useSelector(getTrayTop);
  const depth = useSelector(getTrayDepth);
  const hasBack = depth > 1;
  const [open, type] = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return [
      searchParams.get('open'),
      searchParams.get('type')
    ];
  }, [location]);
  const TrayComponent = useMemo(() => {
    const trayType = tray.trayType || null;
    switch (trayType) {
    case ADD_TO_GROUP_TRAY:
      return AddToGroupTray;
    case ENTITY_TRAY:
      return Entity;
    case LAYER_TRAY:
      return Layer;
    case GROUP_TRAY:
      return Group;
    case OVERLAP_TRAY:
      return OverlapTray;
    case RECENT_TRAY:
      return Recent;
    case SEARCH_TRAY:
      return Search;
    case FILTER_TRAY:
      return Filter;
    case DETAILS_TRAY:
      return Details;
    default:
      return null;
    }
  }, [tray]);

  useEffect(() => {
    const isMapTray = isMapPage(location);
    if (isMapTray && R.isEmpty(tray)) {
      dispatch(openFilterTray());
    }
  }, [dispatch, location, tray]);

  useEffect(() => {
    if (open && type) {
      const id = parseInt(open, 10);
      dispatch(openEntityTray({
        id,
        type
      }));
    }
  }, [dispatch, open, type]);

  useWatchOverlapEntitiesCache();

  if (!TrayComponent) {
    return null;
  }
  // There are problems with the slide out functionality:
  const slideOutStyle = '';
  const isMapTray = isMapPage(location);
  return (
    <div
      data-testid="map-tray"
      className={classNames(
        styles.mapTrayContainer,
        slideOutStyle,
        {
          [styles.mapTrayContainerNoFilter]: !isMapTray,
          [styles.mapTrayContainerAnimationSlideIn]: hasBack
        }
      )}
      style={{
        ...paper,
        boxShadow: 'rgba(0, 0, 0, 0.19) 0px 10px 30px, rgba(0, 0, 0, 0.23) 0px 6px 10px'
      }}
    >
      { TrayComponent && <TrayComponent /> }
    </div>
  );
};

export default memo(Tray);
